import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Archive, ArchiveRestore, Loader2, AlertCircle, Briefcase } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getRoleService } from '@/services/roleService';
import type { RoleListItem } from '@/types/role';
import { RoleDialog } from '@/components/RolesAndRates/RoleDialog';
import { RoleDetailDialog } from '@/components/RolesAndRates/RoleDetailDialog';
import { ProjectRoleOverridesTab } from '@/components/RolesAndRates/ProjectRoleOverridesTab';
import { EmployeeRoleOverridesTab } from '@/components/RolesAndRates/EmployeeRoleOverridesTab';
import { RateResolutionDemo } from '@/components/RolesAndRates/RateResolutionDemo';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import { useDataSource } from '@/contexts/DataSourceContext';

interface RolesAndRatesPageProps {
  onNavigate?: (path: string) => void;
}

export const RolesAndRatesPage: React.FC<RolesAndRatesPageProps> = ({ onNavigate }) => {
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('roles-rates');
  const roleService = useMemo(() => getRoleService(screenMode), [screenMode]);
  
  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<RoleListItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  // Debug logging
  useEffect(() => {
    console.log('selectedRoleId changed to:', selectedRoleId);
  }, [selectedRoleId]);

  const loadRoles = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await roleService.getAllRoles();
      setRoles(data);
      filterRoles(data, searchQuery, showArchived);
      if (screenMode === 'live') {
        toast.success('Roles loaded from API', {
          description: `Loaded ${data.length} roles from the database`,
        });
      }
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles. Please try again.');
      toast.error('Failed to load roles', {
        description: screenMode === 'live' ? 'Could not connect to the API. Please check your connection.' : undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [screenMode]);

  useEffect(() => {
    filterRoles(roles, searchQuery, showArchived);
  }, [searchQuery, showArchived, roles]);

  const filterRoles = (rolesList: RoleListItem[], query: string, includeArchived: boolean) => {
    let filtered = rolesList;

    // Filter by status
    if (!includeArchived) {
      filtered = filtered.filter(r => r.status === 'Active');
    }

    // Filter by search query
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(r =>
        r.name.toLowerCase().includes(lowerQuery) ||
        r.description?.toLowerCase().includes(lowerQuery)
      );
    }

    setFilteredRoles(filtered);
  };

  const handleRoleCreated = () => {
    setIsCreateDialogOpen(false);
    loadRoles();
    toast.success('Role created successfully');
  };

  const handleRoleUpdated = () => {
    setSelectedRoleId(null);
    loadRoles();
    toast.success('Role updated successfully');
  };

  const handleArchive = async (roleId: number, roleName: string) => {
    try {
      await roleService.archiveRole(roleId);
      toast.success(`Archived role: ${roleName}`);
      loadRoles();
    } catch (err) {
      console.error('Error archiving role:', err);
      toast.error('Failed to archive role');
    }
  };

  const handleUnarchive = async (roleId: number, roleName: string) => {
    try {
      await roleService.unarchiveRole(roleId);
      toast.success(`Restored role: ${roleName}`);
      loadRoles();
    } catch (err) {
      console.error('Error restoring role:', err);
      toast.error('Failed to restore role');
    }
  };

  const activeCount = roles.filter(r => r.status === 'Active').length;
  const archivedCount = roles.filter(r => r.status === 'Archived').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Roles & Rates"
        subtitle="Manage organizational roles and their associated billing and cost rates"
        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<Briefcase />}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active Roles</CardDescription>
            <CardTitle className="text-3xl">{activeCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Archived Roles</CardDescription>
            <CardTitle className="text-3xl">{archivedCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Roles</CardDescription>
            <CardTitle className="text-3xl">{roles.length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Tabs for Phase 1 and Phase 2 */}
      <Tabs defaultValue="roles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="roles">Base Roles</TabsTrigger>
          <TabsTrigger value="project-overrides">Project Overrides</TabsTrigger>
          <TabsTrigger value="employee-overrides">Employee Overrides</TabsTrigger>
          <TabsTrigger value="rate-demo">Rate Demo</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-4">
          {/* Main Content Card */}
          <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Roles</CardTitle>
              <CardDescription>
                Define job roles and set their billing and cost rates
              </CardDescription>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Role
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={showArchived ? "default" : "outline"}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? <ArchiveRestore className="h-4 w-4 mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
              {showArchived ? 'Hide Archived' : 'Show Archived'}
            </Button>
          </div>

          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredRoles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {searchQuery ? 'No roles match your search' : 'No roles found'}
              </p>
              {!searchQuery && !showArchived && (
                <Button onClick={() => setIsCreateDialogOpen(true)} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create your first role
                </Button>
              )}
            </div>
          )}

          {/* Roles Table */}
          {!isLoading && filteredRoles.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Role</th>
                    <th className="text-right p-4 font-medium">Billing Rate</th>
                    <th className="text-right p-4 font-medium">Cost Rate</th>
                    <th className="text-right p-4 font-medium">Margin</th>
                    <th className="text-center p-4 font-medium">Employees</th>
                    <th className="text-center p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRoles.map((role) => (
                    <tr
                      key={role.id}
                      className="hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => {
                        console.log('Row clicked, setting roleId:', role.id);
                        setSelectedRoleId(role.id);
                      }}
                    >
                      <td className="p-4">
                        <div>
                          <div className="font-medium">{role.name}</div>
                          {role.description && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {role.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        {role.currentBillingRate ? (
                          <span className="font-mono">
                            {formatCurrency(role.currentBillingRate, role.currency)}/hr
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {role.currentCostRate ? (
                          <span className="font-mono">
                            {formatCurrency(role.currentCostRate, role.currency)}/hr
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        {role.marginPercentage !== null && role.marginPercentage !== undefined ? (
                          <span className={`font-medium ${role.marginPercentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatPercentage(role.marginPercentage)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant="secondary">{role.employeeCount}</Badge>
                      </td>
                      <td className="p-4 text-center">
                        <Badge variant={role.status === 'Active' ? 'default' : 'secondary'}>
                          {role.status}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (role.status === 'Active') {
                              handleArchive(role.id, role.name);
                            } else {
                              handleUnarchive(role.id, role.name);
                            }
                          }}
                        >
                          {role.status === 'Active' ? (
                            <>
                              <Archive className="h-4 w-4 mr-1" />
                              Archive
                            </>
                          ) : (
                            <>
                              <ArchiveRestore className="h-4 w-4 mr-1" />
                              Restore
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="project-overrides">
          <Card>
            <CardContent className="pt-6">
              <ProjectRoleOverridesTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employee-overrides">
          <Card>
            <CardContent className="pt-6">
              <EmployeeRoleOverridesTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rate-demo">
          <Card>
            <CardContent className="pt-6">
              <RateResolutionDemo />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <RoleDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={handleRoleCreated}
      />

      {selectedRoleId !== null && (
        <RoleDetailDialog
          roleId={selectedRoleId}
          open={true}
          onOpenChange={(open) => {
            console.log('Dialog onOpenChange called with:', open);
            if (!open) {
              console.log('Closing dialog, setting selectedRoleId to null');
              setSelectedRoleId(null);
            }
          }}
          onSuccess={handleRoleUpdated}
        />
      )}
    </div>
  );
};
